import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';

const initialState = {
    items: [],
    total: 0,
    page: 1,
    limit: 10,
    sortBy: 'createdAt',
    order: 'desc',
    role: 'all',
    status: 'all',
    search: '',
    loading: false,
    error: '',
};

function reducer(state, action) {
    switch (action.type) {
        case 'SET': return { ...state, ...action.payload };
        case 'LOADING': return { ...state, loading: true, error: '' };
        case 'SUCCESS': return {
            ...state,
            loading: false,
            items: action.items,
            total: action.total ?? action.items?.length ?? 0
        };
        case 'ERROR': return { ...state, loading: false, error: action.error };
        default: return state;
    }
}

const ADMIN_API = '/api/admin';
const routes = {
    users: () => `${ADMIN_API}/users`,
};

const useDebounceRef = (value, delay = 500) => {
    const tRef = useRef();
    const ref = useRef(value);
    useEffect(() => {
        clearTimeout(tRef.current);
        tRef.current = setTimeout(() => { ref.current = value; }, delay);
        return () => clearTimeout(tRef.current);
    }, [value, delay]);
    return ref;
};

export default function useAdminUsers() {
    const [state, dispatch] = useReducer(reducer, initialState);
    const { getToken } = useAuth();
    const abortRef = useRef();
    const debouncedSearchRef = useDebounceRef(state.search, 500);

    // wrapper pour ajouter l'Authorization du AuthContext
    const withAuth = useCallback(async (fn) => {
        const token = typeof getToken === 'function' ? getToken() : null;
        const cfg = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
        // fn doit être une fonction qui retourne une promesse axios
        return await fn(cfg);
    }, [getToken]);

    const load = useCallback(async () => {
        if (abortRef.current) abortRef.current.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        dispatch({ type: 'LOADING' });
        try {
            const params = {
                page: state.page,
                limit: state.limit,
                sortBy: state.sortBy,
                order: state.order,
                role: state.role !== 'all' ? state.role : undefined,
                status: state.status !== 'all' ? state.status : undefined,
                search: debouncedSearchRef.current || undefined,
            };

            const { data } = await withAuth((cfg) =>
                axios.get(routes.users(), { ...cfg, params, signal: controller.signal })
            );

            const items = data?.items || data || [];
            const total = data?.total ?? (Array.isArray(data) ? data.length : 0);
            dispatch({ type: 'SUCCESS', items, total });
        } catch (e) {
            if (e.name === 'CanceledError' || e.message === 'canceled') return;
            dispatch({ type: 'ERROR', error: e?.response?.data?.error || e.message || 'Erreur chargement utilisateurs' });
        }
    }, [state.page, state.limit, state.sortBy, state.order, state.role, state.status, debouncedSearchRef, withAuth]);

    useEffect(() => { load(); }, [load, debouncedSearchRef.current]); // eslint-disable-line

    const pageCount = useMemo(
        () => Math.max(1, Math.ceil(state.total / state.limit)),
        [state.total, state.limit]
    );

    const setQuery = useCallback((patch) => {
        dispatch({ type: 'SET', payload: patch });
    }, []);

    // optimistic toggle (le backend est appelé via callback)
    const toggleUserOptimistic = useCallback(async (id, doRequest) => {
        // apply optimistic
        dispatch({
            type: 'SET',
            payload: {
                items: state.items.map(u => u._id === id ? { ...u, isActive: !u.isActive } : u)
            }
        });

        try {
            await doRequest();
        } catch (e) {
            // rollback
            dispatch({
                type: 'SET',
                payload: {
                    items: state.items.map(u => u._id === id ? { ...u, isActive: !u.isActive } : u)
                }
            });
            throw e;
        }
    }, [state.items]);

    return { state, setQuery, load, pageCount, toggleUserOptimistic, withAuth };
}
